// PfEntity.jsx
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Toast from "./Toast";
import PfEquity_Shareholders from "./PfEquity_Shareholders";

import PfEntity_Company from "./PfEntity_Company";
/**
 * PfEntity - main component.
 * - Uses PfEntity_Shareholders for the shareholders section (previously directors).
 * - Keeps shareholders as a safe array, normalizes shapes for dirty-check.
 * - On submit, sends 'directors' field to backend (keeps legacy backend key).
 */

export default function PfEntity({ onDirtyChange }) {
  const navigate = useNavigate();
  const { id } = useParams();

  const emptyForm = {
    entityType: "Project",
    holdingcompany: "",
    companyName: "",
    registrar: "",
    incorporationDate: "",
    registeredAddress: "",
    registeredCapital: "",
    paidUpCapital: "",
    shareValue: "",
    numberOfShares: "",
    initialValuation: ""
  };

  // State
  const [form, setForm] = useState(emptyForm);
  const [shareholders, setShareholders] = useState([]); // formerly 'directors'
  const [initialForm, setInitialForm] = useState(emptyForm);
  const [initialShareholders, setInitialShareholders] = useState([]);
  const [isDirty, setIsDirty] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");
  const [saveType, setSaveType] = useState("success");

  // helper: normalize incoming 'project.shareholders' or 'project.directors'
  const safeShareholdersFromServer = (raw) => {
    if (!raw && raw !== 0) return [];
    if (Array.isArray(raw)) return raw.map(normalizeShareholder);
    if (typeof raw === "string") {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed.map(normalizeShareholder);
        raw = parsed;
      } catch (e) {
        return [];
      }
    }
    if (typeof raw === "object" && raw !== null) {
      // if it's an object keyed by index, take values
      const vals = Object.values(raw);
      if (Array.isArray(vals)) return vals.map(normalizeShareholder);
    }
    return [];
  };

  function normalizeShareholder(s) {
    const name = s && s.name != null ? String(s.name) : "";
    const role = s && s.role != null ? String(s.role) : "";
    const shares = s && (s.shares !== undefined && s.shares !== null) ? Number(s.shares) : "";
    const holdingPercent = s && (s.holdingPercent !== undefined && s.holdingPercent !== null) ? Number(s.holdingPercent) : "";
    return { name, role, shares, holdingPercent };
  }

  // load project
  useEffect(() => {
    if (!id) return;

    fetch(`${process.env.REACT_APP_API_BASE_URL}/projects`)
      .then((res) => res.json())
      .then((data) => {
        const project = (data.lists || []).find((p) => String(p.id) === String(id));
        if (!project) return;

        // backend might store shareholders under 'directors' or 'shareholders'
        let rawShareholders = project.shareholders ?? project.directors ?? [];

        // parse if string
        if (typeof rawShareholders === "string") {
          try {
            rawShareholders = JSON.parse(rawShareholders);
          } catch (e) {
            rawShareholders = [];
          }
        }

        const safe = safeShareholdersFromServer(rawShareholders);

        const loadedForm = {
          entityType: project.entityType || "Project",
          holdingcompany: project.holdingcompany || "",
          companyName: project.companyName || "",
          registrar: project.registrar || "",
          incorporationDate: project.incorporationDate || "",
          registeredAddress: project.registeredAddress || "",
          registeredCapital: project.registeredCapital || "",
          paidUpCapital: project.paidUpCapital || "",
          shareValue: project.shareValue || "",
          numberOfShares: project.numberOfShares || "",
          initialValuation: project.initialValuation || ""
        };

        setForm(loadedForm);
        setShareholders(safe);
        setInitialForm(loadedForm);
        setInitialShareholders(safe);
      })
      .catch((err) => {
        console.error("Failed loading project:", err);
      });
  }, [id]);

  // dirty-check
  useEffect(() => {
    const changed =
      JSON.stringify(form) !== JSON.stringify(initialForm) ||
      JSON.stringify(shareholders) !== JSON.stringify(initialShareholders);
    setIsDirty(changed);
    if (onDirtyChange) onDirtyChange(changed);
  }, [form, shareholders, initialForm, initialShareholders, onDirtyChange]);

  // validation
  const validateForm = () => {
    if (form.entityType === "Company") {
      if (!form.companyName) return "Company Name is required";
      if (!form.registeredCapital) return "Registered Capital is required";
      if (!form.paidUpCapital) return "Paid-up Capital is required";
      if (!Array.isArray(shareholders) || shareholders.length === 0) return "At least one shareholder is required";
    }
    return null;
  };

  // submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSaveMessage("");

    const error = validateForm();
    if (error) {
      setSaveMessage(`❌ ${error}`);
      setSaveType("error");
      setLoading(false);
      return;
    }

    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));

      // keep backend compatibility: send as "directors" (legacy) and also "shareholders"
      fd.append("directors", JSON.stringify(shareholders));
      fd.append("shareholders", JSON.stringify(shareholders));

      if (id) fd.append("id", id);
      const endpoint = id ? "updateProject" : "addProject";

      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/${endpoint}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${process.env.REACT_APP_ADMIN_TOKEN}` },
        body: fd
      });

      const text = await res.text();
      const data = JSON.parse(text || "{}");

      if (data.success) {
        setSaveMessage("✅ Saved successfully!");
        setSaveType("success");

        // Reset baseline
        setInitialForm({ ...form });
        setInitialShareholders([...shareholders]);
        setIsDirty(false);
      } else {
        setSaveMessage("❌ Save failed");
        setSaveType("error");
      }
    } catch (err) {
      console.error("Network error:", err);
      setSaveMessage("❌ Network error");
      setSaveType("error");
    } finally {
      setLoading(false);
    }
  };

  // handlers
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCancel = () => {
    setForm(initialForm);
    setShareholders(initialShareholders);
    setSaveMessage("");
    setIsDirty(false);
    if (onDirtyChange) onDirtyChange(false);
  };

  // derived totals
  const totalShareholderShares = shareholders.reduce((s, sh) => s + Number(sh.shares || 0), 0);
  const totalHoldingPercent = shareholders.reduce((s, sh) => s + Number(sh.holdingPercent || 0), 0);
  const remainingShares = (Number(form.numberOfShares) || 0) - totalShareholderShares;

  // small helper row if you want to keep inline preview (not required)
  const ShareholderRow = ({ s, i }) => (
    <div style={{ display: "flex", gap: 10, marginBottom: 8, alignItems: "center" }}>
      <div style={{ minWidth: 140 }}>{s.name || "—"}</div>
      <div style={{ minWidth: 160 }}>{s.role || "—"}</div>
      <div style={{ width: 120 }}>{s.shares}</div>
      <div style={{ width: 120 }}>{s.holdingPercent}</div>
    </div>
  );

  return (
    <div>


      <form onSubmit={handleSubmit} style={{ border: isDirty ? "2px dotted red" : "none", padding: 10 }}>


      <div className="w3-right" style={{ marginTop: 20 ,marginBottom: 20 }}>
          <button type="button" className="btn-muted" style={{ marginLeft: 8 }} onClick={handleCancel}>Cancel</button>

          <button className="w3-button w3-teal w3-wide" type="submit" disabled={!isDirty || loading}>
            {loading ? "Saving..." : "Save"}
          </button>
        </div>


        <div className="w3-row form-row">
          <label>Entity Type</label>
          <select name="entityType" value={form.entityType} onChange={handleChange}>
            <option value="Project">Project</option>
            <option value="Company">Company</option>
          </select>
        </div>

        {form.entityType === "Project" && (
          <div className="form-row">
            <label>Holding Company</label>
            <input name="holdingcompany" value={form.holdingcompany} onChange={handleChange} />
          </div>
        )}

        {form.entityType === "Company" && (
          <>
             {/* Shareholders section (separate component) */}
            <PfEntity_Company
            form={form}
            handleChange={handleChange}
            />

          
          </>
        )}

        <div style={{ marginTop: 20 }}>
          <button className="w3-button w3-teal w3-wide" type="submit" disabled={!isDirty || loading}>
            {loading ? "Saving..." : "Save"}
          </button>
          <button type="button" className="btn-muted" style={{ marginLeft: 8 }} onClick={handleCancel}>Cancel</button>
        </div>

        <Toast message={saveMessage} type={saveType} onClose={() => setSaveMessage("")} />
      </form>

      {/* Optional debug / preview rows */}
      {/* <div style={{ marginTop: 20 }}>
        <h5>Preview</h5>
        {shareholders.map((s, i) => <ShareholderRow s={s} i={i} key={i} />)}
      </div> */}
    </div>
  );
}
